import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { number, t } from "../i18n";
import { sounds, type Sound } from "./sounds";

const samples: { sound: Sound; label: string; ringtone?: boolean }[] = [
  { sound: "incoming", label: "مكالمة واردة", ringtone: true },
  { sound: "outgoing", label: "انتظار الرد", ringtone: true },
  { sound: "connected", label: "بدء المكالمة" },
  { sound: "ended", label: "نهاية المكالمة" },
  { sound: "reconnecting", label: "انقطاع الاتصال" },
  { sound: "restored", label: "عودة الاتصال" },
  { sound: "message", label: "رسالة جديدة" },
];

export function SoundSettings({ inCall }: { inCall: boolean }) {
  const preferences = useSyncExternalStore(
    sounds.subscribe,
    sounds.getPreferences,
  );
  const [playing, setPlaying] = useState("");
  const [failed, setFailed] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
      sounds.stop("preview");
    },
    [],
  );
  useEffect(() => {
    if (inCall) sounds.stop("preview");
  }, [inCall]);
  function preview(sound: Sound, label: string) {
    if (timer.current) clearTimeout(timer.current);
    const played = sounds.play(sound, "preview");
    setFailed(!played);
    setPlaying(played ? label : "");
    timer.current = setTimeout(() => setPlaying(""), 900);
  }
  return (
    <>
      <label className="setting-row">
        <span className="setting-text">
          <strong id="ringtone-label">{t("رنين المكالمات")}</strong>
          <small id="ringtone-help">
            {t("رنين المكالمات الواردة وانتظار الرد داخل رنّة.")}
          </small>
        </span>
        <input
          type="checkbox"
          role="switch"
          className="switch"
          checked={preferences.ringtone}
          aria-checked={preferences.ringtone}
          aria-labelledby="ringtone-label"
          aria-describedby="ringtone-help"
          onChange={(event) =>
            sounds.setPreferences({ ringtone: event.target.checked })
          }
        />
      </label>
      <label className="setting-row">
        <span className="setting-text">
          <strong id="effects-label">{t("المؤثرات الصوتية")}</strong>
          <small id="effects-help">
            {t(
              "نغمات للمكالمات والرسائل الجديدة. تصمت الرسائل أثناء المكالمة.",
            )}
          </small>
        </span>
        <input
          type="checkbox"
          role="switch"
          className="switch"
          checked={preferences.effects}
          aria-checked={preferences.effects}
          aria-labelledby="effects-label"
          aria-describedby="effects-help"
          onChange={(event) =>
            sounds.setPreferences({ effects: event.target.checked })
          }
        />
      </label>
      <label className="setting-row sound-volume">
        <span className="setting-text">
          <strong>{t("مستوى أصوات التطبيق")}</strong>
          <small>
            {t("على هذا المتصفح. لا يغير صوت الطرف الآخر أو إشعارات النظام.")}
          </small>
        </span>
        <span className="sound-volume-control">
          <input
            type="range"
            min="0"
            max="100"
            step="5"
            value={Math.round(preferences.volume * 100)}
            aria-label={t("مستوى أصوات التطبيق")}
            aria-valuetext={`${number(Math.round(preferences.volume * 100))}%`}
            onChange={(event) =>
              sounds.setPreferences({
                volume: Number(event.target.value) / 100,
              })
            }
          />
          <output>{number(Math.round(preferences.volume * 100))}%</output>
        </span>
      </label>
      <div className="setting-row">
        <details
          className="sound-preview"
          onToggle={(event) => {
            if (!event.currentTarget.open) {
              sounds.stop("preview");
              setPlaying("");
            }
          }}
        >
          <summary>{t("تجربة الأصوات")}</summary>
          <div className="sound-samples">
            {samples.map(({ sound, label, ringtone }) => (
              <button
                key={sound}
                type="button"
                className="button secondary small"
                disabled={
                  inCall ||
                  preferences.volume === 0 ||
                  !(ringtone ? preferences.ringtone : preferences.effects)
                }
                data-playing={playing === label || undefined}
                onClick={() => preview(sound, label)}
              >
                {t(label)}
              </button>
            ))}
          </div>
          <small className="sound-preview-status" role="status">
            {inCall
              ? t("تجربة الأصوات متاحة بعد انتهاء المكالمة.")
              : failed
                ? t("تعذر تشغيل الصوت. حاول مجددًا.")
                : playing
                  ? t("جارٍ تشغيل: {sound}", { sound: t(playing) })
                  : t("اختر صوتًا للاستماع إليه.")}
          </small>
        </details>
      </div>
    </>
  );
}
